import React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TENANT_DASHBOARD_PATH } from '@/config/constants';
import { useAuth } from '@/contexts/auth-context';

export function PartnerAuthPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const { authenticateWithPartnerToken } = useAuth();

  const [isAuthenticating, setIsAuthenticating] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const authenticateFromPartner = async () => {
      try {
        if (!tenantSlug) {
          setError('Tenant slug is required');
          return;
        }

        const encodedToken = searchParams.get('t');
        if (!encodedToken) {
          setError('Token is required');
          return;
        }

        const result = await authenticateWithPartnerToken(tenantSlug, encodedToken);
        if (!result) {
          setError('Invalid credentials');
          return;
        }

        navigate(TENANT_DASHBOARD_PATH);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to authenticate with partner token');
        }
      } finally {
        if (isMounted) {
          setIsAuthenticating(false);
        }
      }
    };

    void authenticateFromPartner();

    return () => {
      isMounted = false;
    };
  }, [authenticateWithPartnerToken, navigate, searchParams, tenantSlug]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-foreground">Partner authentication</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Validating your access and signing you in.
        </p>

        {isAuthenticating && !error && (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Authenticating...
          </div>
        )}

        {error && (
          <>
            <Alert className="mt-4 border-destructive/40 bg-destructive/10 text-destructive">
              <ShieldAlert className="size-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button asChild className="mt-4 w-full">
              <Link to="/signin">Go to tenant sign in</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
